/**
 * FileListView — Table View for Files & Folders
 *
 * Renders files and folders in a table using the project's standard
 * Table component (TanStack React Table) and Pagination component.
 *
 * Styling matches ManageDocumentsTable exactly:
 * - Same Table props (cellClassName, maxHeight, extraClass)
 * - Same column header text styles (text-md font-medium text-gray-375)
 * - Same cell text styles (text-md for names, text-sm for metadata)
 * - Same SortByIcon color (text-verba-blue)
 */

import { useMemo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { ColumnDef } from "@tanstack/react-table";
import Table from "../../../../../../../../components/Table/Table";
import Pagination from "../../../../../../../../components/Table/Pagination";
import SortByIcon from "../../../../../../../../assets/svg/sort-by-icon";
import GalleryIcon from "../../../../../../../../assets/svg/gallery-icon";
import OpenFolderIcon from "../../../../../../../../assets/svg/open-folder-icon";
import ChevronRightIcon from "../../../../../../../../assets/svg/chevron-right-icon";
import RowActionsMenu from "./RowActionsMenu";
import type {
  KBFileEntity,
  KBFileSortKey,
  KBFileSortOrder,
} from "../types/fileManagement.types";
import {
  VISIBILITY_LABELS,
  FILE_ACTION_MENU_ITEMS,
  FOLDER_ACTION_MENU_ITEMS,
  DEFAULT_SORT,
  SORTABLE_FIELDS,
  SORT_OPTIONS,
} from "../constants/fileManagement.constants";
import {
  isFolder,
  formatFileSize,
  formatDate,
  getIconForFileType,
  calculateSortMenuPosition,
} from "../utils/fileManagement.utils";
import type { FileListViewProps } from "../types/fileManagement.types";

const FileListView = ({
  items,
  isFetching,
  sortField,
  sortOrder,
  onSort,
  onFolderClick,
  onFileClick,
  onRowAction,
  currentPage,
  rowsPerPage,
  totalPages,
  onPageChange,
  onRowsPerPageChange,
}: FileListViewProps) => {
  // -------------------------------------------------------------------------
  // Internal state: sort dropdown + column pinning
  // -------------------------------------------------------------------------

  const [sortMenu, setSortMenu] = useState<{
    key: string;
    x: number;
    y: number;
  } | null>(null);

  // -------------------------------------------------------------------------
  // Internal handlers
  // -------------------------------------------------------------------------

  const resetToDefaultSort = useCallback(() => {
    onSort(DEFAULT_SORT.sort_key, DEFAULT_SORT.sort_order);
    setSortMenu(null);
  }, [onSort]);

  const handleSortAction = useCallback(
    (field: KBFileSortKey, order: KBFileSortOrder) => {
      const isSameSort = sortField === field && sortOrder === order;
      if (isSameSort) {
        resetToDefaultSort();
        return;
      }
      onSort(field, order);
      setSortMenu(null);
    },
    [sortField, sortOrder, onSort, resetToDefaultSort]
  );

  const handleOpenSortMenu = useCallback(
    (accessorKey: string, triggerRect: DOMRect) => {
      const optionsCount = SORT_OPTIONS[accessorKey]?.length ?? 0;
      const position = calculateSortMenuPosition(triggerRect, optionsCount);
      setSortMenu({ key: accessorKey, x: position.x - 2, y: position.y + 8 });
    },
    []
  );

  const handleRowClick = useCallback(
    (row: KBFileEntity) => {
      if (isFolder(row)) {
        onFolderClick(row);
      } else {
        onFileClick(row);
      }
    },
    [onFolderClick, onFileClick]
  );

  // -------------------------------------------------------------------------
  // Column header renderer with sort dropdown + pin button
  // -------------------------------------------------------------------------

  const renderColumnHeader = useCallback(
    (accessorKey: string, label: string) => {
      const isSortable =
        items.length > 1 &&
        (SORTABLE_FIELDS as readonly string[]).includes(accessorKey);

      return (
        <div
          className={`flex items-center gap-2 h-[34px] px-3 ${
            isSortable ? "justify-between" : ""
          } group`}
        >
          <div
            className={`flex items-center gap-[4px] ${
              isSortable ? "cursor-pointer flex-1" : ""
            }`}
            onClick={
              isSortable
                ? (e) =>
                    handleOpenSortMenu(
                      accessorKey,
                      e.currentTarget.getBoundingClientRect()
                    )
                : undefined
            }
          >
            <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
              {label}
            </span>
            {isSortable && (
              <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
            )}
          </div>
        </div>
      );
    },
    [items.length, handleOpenSortMenu]
  );

  // -------------------------------------------------------------------------
  // Column definitions
  // -------------------------------------------------------------------------

  const columns: ColumnDef<KBFileEntity>[] = useMemo(
    () => [
      {
        accessorKey: "file_icon",
        size: 42,
        header: () => (
          <div className="flex items-center justify-center h-[34px] px-3">
            <GalleryIcon className="w-[14px] h-[14px] text-gray-375 dark:text-gray-375-dk" />
          </div>
        ),
        cell: ({ row }) => {
          if (isFolder(row.original)) {
            return (
              <div className="flex items-center justify-center px-3">
                <OpenFolderIcon className="w-[18px] h-[18px] text-analytics-blue dark:text-analytics-blue-dk" />
              </div>
            );
          }
          const iconSrc = getIconForFileType(row.original.type);
          return (
            <div className="flex items-center justify-center px-3">
              <img
                src={iconSrc}
                alt={row.original.name}
                className="w-[18px] h-[18px]"
              />
            </div>
          );
        },
      },

      // --- Name (sortable) ---
      {
        accessorKey: "name",
        minSize: 300,
        header: () => renderColumnHeader("name", "Name"),
        cell: ({ row }) => {
          const entity = row.original;

          return (
            <div className="px-3">
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk hover:underline cursor-pointer group-hover/row:text-verba-blue dark:group-hover/row:text-verba-blue-dk">
                {entity?.name}
              </span>
            </div>
          );
        },
      },

      // --- File URL Visibility ---
      {
        accessorKey: "visibility",
        size: 140,
        maxSize: 160,
        header: () => renderColumnHeader("visibility", "File URL Visibility"),
        cell: ({ row }) => {
          const entity = row.original;
          if (isFolder(entity) || !entity.visibility) {
            return <div className="px-3" />;
          }
          return (
            <div className="px-3">
              <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
                {VISIBILITY_LABELS[entity.visibility] || entity.visibility}
              </span>
            </div>
          );
        },
      },

      // --- Format ---
      {
        accessorKey: "type",
        size: 80,
        maxSize: 100,
        header: () => renderColumnHeader("type", "Format"),
        cell: ({ row }) => (
          <div className="px-3">
            <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
              {row.original.type === "folder" ? "" : row.original.type}
            </span>
          </div>
        ),
      },

      // --- Size ---
      {
        accessorKey: "size_in_bytes",
        size: 80,
        maxSize: 100,
        header: () => renderColumnHeader("size_in_bytes", "Size"),
        cell: ({ row }) => (
          <div className="px-3">
            <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
              {formatFileSize(row.original.size_in_bytes)}
            </span>
          </div>
        ),
      },

      // --- Updated (sortable) ---
      {
        accessorKey: "updated_at",
        size: 180,
        maxSize: 220,
        header: () => renderColumnHeader("updated_at", "Updated"),
        cell: ({ row }) => (
          <div className="px-3">
            <span className="text-md font-normal text-dark-gray dark:text-dark-gray-dk">
              {formatDate(row.original.updated_at)}
            </span>
          </div>
        ),
      },

      // --- Chevron (folders only, visible on row hover) ---
      {
        accessorKey: "chevron",
        size: 34,
        header: () => <div className="h-[34px]" />,
        cell: ({ row }) => {
          if (!isFolder(row.original)) return <div />;
          return (
            <div className="flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity duration-200">
              <ChevronRightIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
            </div>
          );
        },
      },

      // --- Actions (all rows) ---
      {
        accessorKey: "actions",
        size: 42,
        header: () => <div className="h-[34px]" />,
        cell: ({ row }) => {
          const entity = row?.original;
          const fileMenuItems = entity?.short_url
            ? FILE_ACTION_MENU_ITEMS
            : FILE_ACTION_MENU_ITEMS?.filter((m) => m?.action !== "copy_link");
          const menuItems = isFolder(entity)
            ? FOLDER_ACTION_MENU_ITEMS
            : fileMenuItems;
          return (
            <div className="relative h-full flex items-center justify-center px-2">
              <RowActionsMenu
                entity={entity}
                menuItems={menuItems}
                onRowAction={onRowAction}
              />
            </div>
          );
        },
      },
    ],
    [renderColumnHeader, onRowAction]
  );

  return (
    <div className="flex flex-col gap-3">
      <Table
        COLUMNS={columns}
        COLUMNS_DATA={items}
        cellClassName="!h-[50px] !px-0"
        ariaLabel="Files and folders table"
        isLoading={isFetching}
        extraClass="min-h-[calc(100dvh-400px)] overflow-y-auto no-scrollbar"
        onRowClick={handleRowClick}
      />

      {totalPages > 0 && (
        <Pagination
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={onRowsPerPageChange}
          currentPage={currentPage}
          handlePageChange={onPageChange}
          totalPages={totalPages}
        />
      )}

      {sortMenu &&
        SORT_OPTIONS[sortMenu.key] &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-99"
              onClick={() => setSortMenu(null)}
            />
            <div
              className="fixed z-100 min-w-[160px] p-3 flex flex-col gap-y-1 border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-lg shadow-popper-blur-effect backdrop-blur-2xl"
              style={{ left: sortMenu.x, top: sortMenu.y }}
              data-popup-content
            >
              {SORT_OPTIONS[sortMenu.key].map((option) => (
                <div
                  key={option.order}
                  className={`flex items-center gap-1 p-2 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg ${
                    sortField === sortMenu.key && sortOrder === option.order
                      ? "bg-card-highlight dark:bg-card-highlight-dk"
                      : ""
                  }`}
                  onClick={() =>
                    handleSortAction(
                      sortMenu.key as KBFileSortKey,
                      option.order
                    )
                  }
                >
                  <option.Icon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
                  <span className="text-md text-heading-text dark:text-heading-text-dk">
                    {option.label}
                  </span>
                </div>
              ))}
              <div
                className="text-left text-sm font-medium cursor-pointer text-verba-blue dark:text-verba-blue-dk mt-4 hover:underline"
                onClick={resetToDefaultSort}
              >
                Reset
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default FileListView;
